import { RootState } from '../../../store'
import { createSelector } from '@reduxjs/toolkit'
import { InvitedTeamMember, InvitedTeamMemberReport } from './types'

export const selectInvitedTeamMemberReport = (state: RootState) => state.invitedTeamMemberPerEvent.data
export const selectInvitedTeamMembersLoading = (state: RootState) =>
  state.invitedTeamMemberPerEvent.loading
export const selectInvitedTeamMembersError = (state: RootState) => state.invitedTeamMemberPerEvent.error
export const selectInvitedTeamMembersFilters = (state: RootState) =>
  state.invitedTeamMemberPerEvent.filters

export const selectFilteredInvitedTeamMembers = createSelector(
  [selectInvitedTeamMemberReport, selectInvitedTeamMembersFilters],
  (report: InvitedTeamMemberReport | null, filters) => {
    if (!report) return null

    return {
      ...report,
      invitedTeamMembers: report.invitedTeamMembers.filter((member: InvitedTeamMember) => {
        const matchesEventStatus =
          !filters.eventStatus ||
          (filters.eventStatus === 'Yes' && member.eventStatus.toLowerCase() === 'registered') ||
          (filters.eventStatus === 'No' && member.eventStatus.toLowerCase() === 'unregistered')
        const matchesJobProfile = !filters.jobProfile || member.jobProfile === filters.jobProfile
        const matchesFunctionalArea =
          !filters.functionalArea || member.functionalArea === filters.functionalArea
        const matchesSupervisor =
          !filters.supervisorName || member.supervisorName === filters.supervisorName
        const matchesOperationalManager =
          !filters.operationalManagerName ||
          member.operationalManagerName === filters.operationalManagerName
        const matchesTidsPractice = !filters.tidsPractice || member.tidsPractice === filters.tidsPractice

        const matchesSearch =
          !filters.searchQuery ||
          member.fullName.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
          member.email.toLowerCase().includes(filters.searchQuery.toLowerCase())

        return (
          matchesEventStatus &&
          matchesJobProfile &&
          matchesFunctionalArea &&
          matchesSupervisor &&
          matchesTidsPractice &&
          matchesSearch &&
          matchesOperationalManager
        )
      }),
    }
  },
)

export const selectFilteredInvitedTeamMembersCount = createSelector(
  [selectFilteredInvitedTeamMembers],
  (filteredReport) => {
    if (!filteredReport)
      return {
        totalInvited: 0,
        registered: { count: 0, percentage: 0 },
        unregistered: { count: 0, percentage: 0 },
      }

    const totalInvited = filteredReport.invitedTeamMembers.length

    const registeredMembers = filteredReport.invitedTeamMembers.filter(
      (member) => member.eventStatus.toLowerCase() === 'registered',
    )
    const unregisteredMembers = filteredReport.invitedTeamMembers.filter(
      (member) => member.eventStatus.toLowerCase() === 'unregistered',
    )

    const registeredCount = registeredMembers.length
    const unregisteredCount = unregisteredMembers.length

    const registeredPercentage =
      totalInvited > 0 ? ((registeredCount / totalInvited) * 100).toFixed(2) : 0
    const unregisteredPercentage =
      totalInvited > 0 ? ((unregisteredCount / totalInvited) * 100).toFixed(2) : 0

    return {
      totalInvited,
      registered: {
        count: registeredCount,
        percentage: registeredPercentage,
      },
      unregistered: {
        count: unregisteredCount,
        percentage: unregisteredPercentage,
      },
      targetCompliance: filteredReport.targetCompliance,
    }
  },
)

export const selectEventStatusOptions = createSelector([selectInvitedTeamMemberReport], () => {
  return ['Yes', 'No']
})

export const selectJobProfileOptions = createSelector([selectFilteredInvitedTeamMembers], (report) => {
  if (!report) return []
  return Array.from(new Set(report.invitedTeamMembers.map((member) => member.jobProfile))).sort()
})

export const selectFunctionalAreaOptions = createSelector(
  [selectFilteredInvitedTeamMembers],
  (report) => {
    if (!report) return []
    return Array.from(new Set(report.invitedTeamMembers.map((member) => member.functionalArea))).sort()
  },
)

export const selectSupervisorOptions = createSelector([selectFilteredInvitedTeamMembers], (report) => {
  if (!report) return []
  return Array.from(new Set(report.invitedTeamMembers.map((member) => member.supervisorName))).sort()
})

export const selectOperationalManagerOptions = createSelector(
  [selectFilteredInvitedTeamMembers],
  (report) => {
    if (!report) return []
    return Array.from(
      new Set(report.invitedTeamMembers.map((member) => member.operationalManagerName)),
    ).sort()
  },
)

export const selectTidsPracticeOptions = createSelector([selectFilteredInvitedTeamMembers], (report) => {
  if (!report) return []
  return Array.from(new Set(report.invitedTeamMembers.map((member) => member.tidsPractice))).sort()
})

// Helper function to calculate group statistics
const calculateGroupStatistics = (
  members: InvitedTeamMember[],
  groupKey: keyof InvitedTeamMember,
): Record<string, { total: number; registered: number; unregistered: number }> => {
  return members.reduce(
    (acc, member) => {
      const groupName = member[groupKey] as string

      if (!acc[groupName]) {
        acc[groupName] = { total: 0, registered: 0, unregistered: 0 }
      }

      acc[groupName].total++

      if (member.eventStatus.toLowerCase() === 'registered') {
        acc[groupName].registered++
      } else {
        acc[groupName].unregistered++
      }

      return acc
    },
    {} as Record<string, { total: number; registered: number; unregistered: number }>,
  )
}

export const selectGroupedTeamMemberReport = createSelector(
  [selectFilteredInvitedTeamMembers, selectInvitedTeamMembersFilters],
  (filteredReport, filters) => {
    if (!filteredReport) return {}

    // Check if no filters are applied
    const hasNoFilters = Object.values(filters).every((filter) => filter === undefined)

    // If no filters, default to TIDS Practice
    if (hasNoFilters) {
      return {
        data: calculateGroupStatistics(filteredReport.invitedTeamMembers, 'tidsPractice'),
      }
    }

    // If filtered by TIDS Practice only, show operational managers
    if (filters.tidsPractice && !filters.operationalManagerName) {
      const filteredByOperationalManager = filteredReport.invitedTeamMembers.filter(
        (member) => member.tidsPractice === filters.tidsPractice,
      )

      return {
        data: calculateGroupStatistics(filteredByOperationalManager, 'operationalManagerName'),
      }
    }

    // If filtered by operational manager only, show supervisors
    if (filters.operationalManagerName && !filters.supervisorName) {
      const filteredBySupervisor = filteredReport.invitedTeamMembers.filter(
        (member) => member.operationalManagerName === filters.operationalManagerName,
      )

      return {
        data: calculateGroupStatistics(filteredBySupervisor, 'supervisorName'),
      }
    }

    // Default grouping
    return {
      data: calculateGroupStatistics(filteredReport.invitedTeamMembers, 'tidsPractice'),
    }
  },
)
